import React from "react";
import { Link } from "react-router-dom";
import { Star, Clock, Bike, Utensils, ShoppingBag } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { isRestaurantOpen } from "@/lib/api";

const RestaurantCard = ({ r, index = 0 }) => {
  const { t, cuisineLabel } = useLang();
  const isOpen = r.is_active && isRestaurantOpen(r.opening_hours);

  return (
    <Link
      to={`/restaurant/${r.id}`}
      data-testid={`restaurant-card-${r.slug || r.id}`}
      className="group relative bg-white rounded-2xl overflow-hidden border border-navy/8 card-hover flex flex-col animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-sand-100">
        <img
          src={r.cover_image}
          alt={r.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {r.featured && (
          <div className="absolute top-3 left-3 bg-coral text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
            Featured
          </div>
        )}
        <div className={`absolute top-3 right-3 text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ${isOpen ? "bg-teal text-white" : "bg-navy/80 text-white"}`}>
          {isOpen ? t("open") : t("closed")}
        </div>
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading font-bold text-navy text-lg leading-tight">{r.name}</h3>
          <div className="flex items-center gap-1 text-sm shrink-0">
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
            <span className="font-semibold">{r.rating}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-navy/60">
          <span className="capitalize">{cuisineLabel(r.cuisine)}</span>
          {r.price_range && <><span className="text-navy/30">•</span><span className="font-semibold text-navy">{r.price_range}</span></>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
          {r.dine_in && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal bg-teal/10 px-2 py-0.5 rounded-full">
              <Utensils className="w-2.5 h-2.5" /> Dine-in
            </span>
          )}
          {r.takeaway && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-ocean bg-ocean/10 px-2 py-0.5 rounded-full">
              <ShoppingBag className="w-2.5 h-2.5" /> Takeaway
            </span>
          )}
          {r.delivery && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-coral bg-coral/10 px-2 py-0.5 rounded-full">
              <Bike className="w-2.5 h-2.5" /> Delivery
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 mt-auto pt-2 text-xs text-navy/70">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {r.delivery_time}</span>
          {r.delivery && <span className="flex items-center gap-1" title="€2.99 over €20 orders — otherwise €4.99"><Bike className="w-3 h-3" /> €2.99+</span>}
        </div>
      </div>
    </Link>
  );
};

export default RestaurantCard;
